import type { LucideIcon } from "lucide-react";
import { matchPath, NavLink, useLocation } from "react-router-dom";
import {
  SidebarGroup,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
} from "@/components/ui/sidebar";
import { cn } from "@/lib/utils";

export function NavMain({
  label,
  items,
}: {
  label?: string;
  items: {
    title: string;
    url: string;
    icon: LucideIcon;
    subItems?: {
      title: string;
      url: string;
      end?: boolean;
    }[];
  }[];
}) {
  const { pathname } = useLocation();

  const isItemActive = (url: string, end = false) =>
    Boolean(
      matchPath(
        {
          path: url,
          end,
        },
        pathname
      )
    );

  const isGroupActive = (item: { url: string; subItems?: { url: string }[] }) =>
    isItemActive(item.url) ||
    Boolean(item.subItems?.some((subItem) => isItemActive(subItem.url)));

  return (
    <SidebarGroup>
      {label ? <SidebarGroupLabel>{label}</SidebarGroupLabel> : null}
      <SidebarMenu>
        {items.map((item) => (
          <SidebarMenuItem key={item.title}>
            <SidebarMenuButton
              asChild
              tooltip={item.title}
              isActive={isGroupActive(item)}
            >
              <NavLink
                to={item.url}
                end={item.url === "/dashboard"}
                className={cn("flex w-full items-center gap-2")}
              >
                <item.icon />
                <span>{item.title}</span>
              </NavLink>
            </SidebarMenuButton>
            {item.subItems?.length ? (
              <SidebarMenuSub>
                {item.subItems.map((subItem) => (
                  <SidebarMenuSubItem key={subItem.title}>
                    <SidebarMenuSubButton
                      asChild
                      isActive={isItemActive(subItem.url, subItem.end ?? false)}
                    >
                      <NavLink to={subItem.url} end={subItem.end ?? false}>
                        <span>{subItem.title}</span>
                      </NavLink>
                    </SidebarMenuSubButton>
                  </SidebarMenuSubItem>
                ))}
              </SidebarMenuSub>
            ) : null}
          </SidebarMenuItem>
        ))}
      </SidebarMenu>
    </SidebarGroup>
  );
}
